import {createContext, use, useMemo} from 'react'
import type {Properties} from '@ninetailed/experience.js-shared'

type TrackFunction = (event: string, properties?: Properties) => void

const NinetailedTrackContext = createContext<TrackFunction | null>(null)

const noopTrack: TrackFunction = () => {}

/**
 * Provider that exposes a Ninetailed track function to descendants.
 * Should be rendered inside a NinetailedProvider, with the `track` function
 * from `useNinetailed()` passed as a prop.
 */
export function NinetailedTrackProvider({track, children}: {track: TrackFunction; children: React.ReactNode}) {
  return <NinetailedTrackContext value={track}>{children}</NinetailedTrackContext>
}

/**
 * Returns a Ninetailed track function that is safe to call from any component,
 * whether or not a NinetailedProvider is mounted. When no provider is present,
 * the returned function is a no-op.
 *
 * @example
 * ```tsx
 * const track = useNinetailedTrack()
 * <button onClick={() => track('primary_cta_click', { label: 'Sign up', location: 'hero' })}>
 *   Sign up
 * </button>
 * ```
 */
export function useNinetailedTrack(): TrackFunction {
  const track = use(NinetailedTrackContext)

  return useMemo(() => track ?? noopTrack, [track])
}
